import React, { useCallback, useEffect, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { LookInsideSlide } from '../data/lookInsideSlides';
type LookInsideLightboxProps = {
  slides: LookInsideSlide[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
};
const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/** Full-screen page viewer. Pages are shown at full resolution and scroll vertically. */
export function LookInsideLightbox({
  slides,
  index,
  onClose,
  onChange
}: LookInsideLightboxProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const touch = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const open = index !== null;
  const go = useCallback((delta: number) => {
    if (index === null) return;
    const target = index + delta;
    if (target >= 0 && target < slides.length) onChange(target);
  }, [index, slides.length, onChange]);

  // Lock page scroll, move focus in, and restore focus on close.
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [open]);

  // Each new page starts at its top.
  useEffect(() => {
    if (dialogRef.current) dialogRef.current.scrollTop = 0;
  }, [index]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();else if (e.key === 'ArrowRight') go(1);else if (e.key === 'ArrowLeft') go(-1);else if (e.key === 'Tab' && dialogRef.current) {
        const items = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, go, onClose]);
  if (index === null) return null;
  const slide = slides[index];
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touch.current = {
      x: t.clientX,
      y: t.clientY
    };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touch.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx < 0 ? 1 : -1);
  };
  const navButton = 'fixed top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-[#FFFDF8]/10 text-[#FFFDF8] transition-colors duration-150 hover:bg-[#FFFDF8]/20 disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E3A33B] sm:flex';
  return <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={`Volume 3 page ${index + 1} of ${slides.length}`} className="look-inside-fade fixed inset-0 z-[100] overflow-y-auto overscroll-contain bg-[#2A1830]/95" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-170" data-mp="2o0ybglmaf2hr">
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 sm:px-6" style={{
      backgroundColor: 'rgba(42,24,48,0.85)'
    }} data-id="element-171" data-mp="36u29skaga3ip">
        <span className="rounded-full bg-[#FFFDF8]/10 px-3 py-1 text-sm font-semibold tabular-nums text-[#FFFDF8]" aria-live="polite" data-id="element-172" data-mp="3bugdjq50qrax">
          {index + 1} / {slides.length}
        </span>
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FFFDF8]/10 text-[#FFFDF8] transition-colors duration-150 hover:bg-[#FFFDF8]/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E3A33B]" data-id="element-173" data-mp="20b3xu1u599wg">
          <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-174" data-mp="21x03uenvy9p2" />
        </button>
      </div>

      <button type="button" onClick={() => go(-1)} disabled={index === 0} aria-label="Previous page" className={`${navButton} left-4`} data-id="element-175" data-mp="2ykwopvqcvr63">
        <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-176" data-mp="2tks722dpoqea" />
      </button>
      <button type="button" onClick={() => go(1)} disabled={index === slides.length - 1} aria-label="Next page" className={`${navButton} right-4`} data-id="element-177" data-mp="2pjq53dseu08m">
        <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-178" data-mp="1e5ur48d8rxiz" />
      </button>

      <figure className="mx-auto w-full max-w-[960px] px-4 pb-12 pt-2 sm:px-20" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onClick={e => {
      if (e.target === e.currentTarget) onClose();
    }} data-id="element-179" data-mp="1e3rs4yu42u24">
        <img key={slide.src} src={slide.src} alt={slide.caption} width={slide.width} height={slide.height} className="look-inside-fade mx-auto block h-auto w-full rounded-2xl bg-[#FFFDF8]" style={{
        boxShadow: `0 12px 32px rgba(0,0,0,0.35)`
      }} data-id="element-180" data-mp="3pu9d5iv8ly1d" />
        <figcaption className="mx-auto mt-4 max-w-xl text-center font-['Nunito_Sans',sans-serif] text-[15px] leading-snug text-[#FFFDF8]/85" data-id="element-181" data-mp="7f8kgk5wqxys">
          {slide.caption}
        </figcaption>
      </figure>
      <span className="sr-only" data-id="element-182" data-mp="hefyl9kguwmy">
        Use left and right arrow keys to change page, Escape to close.
      </span>
    </div>;
}